import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import { persistIdentityKey } from './utils/identityBackup';
import {
  runEmailStepRegistration,
  isStillInSignupSteps,
  peekPendingAccessBlockedMessage,
  clearPendingAccessBlockedMessage,
  storePendingAccessBlockedMessage,
  invalidateSignupNavigation,
} from './utils/emailStepRegistration';
import {
  isValidEmail,
  suggestEmailDomain as suggestDomain,
} from './utils/patientContactValidation';
import {
  nextRouteAfterEmailConfirmed,
  prevReachableSignupRoute,
  getSignupVariant,
  isFirstStep,
  usesEmailConfirm,
} from './utils/signupOrderAb';
import {
  markSignupFieldTyped,
  readSignupFieldTypedHere,
} from './utils/signupFieldPrefill';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { takeSignupQuickResumeStep, cancelSignupQuickResume } from './utils/signupQuickResume';
import { usesGuidedCopy, signupUxProgress } from './utils/signupUxAb';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import FieldValidCheck, { FIELD_VALID_CHECK_CSS } from './components/FieldValidCheck';
import SignupStepHeading from './components/SignupStepHeading';
import SignupGreeting, { SIGNUP_GREETING_CSS, readSignupFullName } from './components/SignupGreeting';

const BACKEND_URL = process.env.REACT_APP_API_URL || window.location.origin;

const ACCESS_BLOCKED_DEFAULT =
  'Identificamos, em consulta(s) anterior(es) vinculada(s) a este cadastro, o descumprimento de disposições dos nossos Termos de Uso. Por esse motivo, o acesso a novas consultas na plataforma foi bloqueado.';

function AccessBlockedModal({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-blocked-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10500,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '1rem',
          padding: '1.5rem 1.5rem 1.25rem',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          textAlign: 'left',
        }}
      >
        <h2
          id="email-blocked-title"
          style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', fontWeight: 700, color: '#B91C1C' }}
        >
          Acesso bloqueado
        </h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#334155', lineHeight: 1.55 }}>
          {message}
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.7rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: '#1E293B',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          Entendi
        </button>
      </div>
    </div>
  );
}

const EmailForm = () => {
  const navigate = useNavigate();
  useViewportInsets();

  // Voltar no funil relê o e-mail que esta aba digitou — o rascunho ainda em
  // confirmação tem prioridade sobre o último endereço confirmado.
  const [email, setEmail] = useState(() => {
    const cached =
      readSignupFieldTypedHere('patientEmailDraft')
      || readSignupFieldTypedHere('patientEmail');
    return isValidEmail(cached) ? cached : '';
  });
  const [touched, setTouched] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [blockedMessage, setBlockedMessage] = useState(peekPendingAccessBlockedMessage);
  const inputRef = useRef(null);

  const isValid = isValidEmail(email);
  const guided = usesGuidedCopy();

  /** Grava e-mail definitivo + registration/start; e chama a próxima rota do braço. */
  const persistConfirmedEmailAndGoNext = async (trimmedLower) => {
    const trimmed = trimmedLower;
    sessionStorage.setItem('patientEmail', trimmed);
    localStorage.setItem('patientEmail', trimmed);
    // Backup multi-camada (IDB + cookie) — defesa contra perda de LS.
    try { persistIdentityKey('patientEmail', trimmed); } catch {}
    markSignupFieldTyped('patientEmail');
    try {
      sessionStorage.removeItem('patientEmailDraft');
      localStorage.removeItem('patientEmailDraft');
    } catch {}
    logUxEvent('email_confirmed', { length: trimmed.length });

    const session = await ensureSignupSession().catch(() => null);
    const patientId =
      (session && session.patientId) ||
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';
    const fullName = sessionStorage.getItem('fullName') || localStorage.getItem('fullName') || '';

    const variant = getSignupVariant();
    const dest = nextRouteAfterEmailConfirmed(variant) || '/cpf';
    const startedAt = Date.now();
    navigate(dest);

    runEmailStepRegistration({
      baseUrl: BACKEND_URL,
      email: trimmed,
      patientId,
      fullName,
      source: 'email_form',
      onPaidRecovered: (route) => {
        invalidateSignupNavigation();
        const inSignup = isStillInSignupSteps();
        logUxEvent('email_paid_dup_redirect', { after_ms: Date.now() - startedAt, still_in_signup: inSignup });
        if (inSignup) navigate(route, { replace: true });
      },
      onBlocked: (message, code) => {
        invalidateSignupNavigation();
        cancelSignupQuickResume();
        logUxEvent('email_access_blocked', { code, after_ms: Date.now() - startedAt });
        storePendingAccessBlockedMessage(message || ACCESS_BLOCKED_DEFAULT);
        if (isStillInSignupSteps()) navigate('/email', { replace: true });
      },
    });
  };

  /**
   * Segunda etapa obrigatória (/email/confirm), exceto se o usuário já confirmou
   * este mesmo endereço e só voltou no fluxo (ex.: retorno do CPF), OU se o
   * braço do A/B de ordem não usa a tela de confirmação (`noconfirm_control`).
   */
  const proceedAfterEmailValidated = (finalEmail) => {
    const trimmed = finalEmail.trim().toLowerCase();
    let existing = '';
    try {
      existing = (sessionStorage.getItem('patientEmail') || localStorage.getItem('patientEmail') || '')
        .trim()
        .toLowerCase();
    } catch {
      existing = '';
    }

    const variant = getSignupVariant();
    if (!usesEmailConfirm(variant)) {
      logUxEvent('email_confirm_skipped_variant', {
        length: trimmed.length,
        variant,
      });
      persistConfirmedEmailAndGoNext(trimmed);
      return;
    }

    if (existing && existing === trimmed) {
      logUxEvent('email_confirm_skipped_unchanged', { length: trimmed.length });
      persistConfirmedEmailAndGoNext(trimmed);
      return;
    }

    try {
      sessionStorage.setItem('patientEmailDraft', trimmed);
      localStorage.setItem('patientEmailDraft', trimmed);
    } catch {}
    markSignupFieldTyped('patientEmailDraft');
    logUxEvent('email_confirm_step_started', { length: trimmed.length });
    navigate('/email/confirm');
  };

  const handleConfirmRaw = () => {
    setTouched(true);
    const possibleFix = suggestDomain(email);
    if (possibleFix) {
      setSuggestion(possibleFix);
      logUxEvent('email_typo_blocked', {
        original: email.trim().toLowerCase(),
        suggested: possibleFix,
      });
      return false;
    }
    if (!isValid) {
      logUxEvent('email_validation_error', {
        reason: 'invalid_email',
        length: String(email || '').trim().length,
      });
      return false;
    }

    proceedAfterEmailValidated(email);
    return true;
  };

  const [isSubmitting, handleConfirm] = useFormSubmitGuard(handleConfirmRaw);

  // "Continuar como" na tela do nome: e-mail salvo confirma sozinho.
  useEffect(() => {
    if (!takeSignupQuickResumeStep('email')) return;
    if (!isValid || blockedMessage) {
      cancelSignupQuickResume();
      return;
    }
    logUxEvent('signup_quick_resume_step', { step: 'email' });
    handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptSuggestion = () => {
    if (!suggestion) return;
    const corrected = suggestion;
    setEmail(corrected);
    setSuggestion(null);
    logUxEvent('email_typo_fixed', { corrected });
    if (isValidEmail(corrected)) {
      proceedAfterEmailValidated(corrected);
    }
  };

  const rejectSuggestion = () => {
    if (!suggestion) return;
    setSuggestion(null);
    logUxEvent('email_typo_rejected', { kept: email.trim().toLowerCase() });
    if (isValidEmail(email)) {
      proceedAfterEmailValidated(email);
    }
  };

  const getErrorMessage = () => {
    if (suggestion) return null;
    if (!touched) return ' ';
    const trimmed = String(email || '').trim();
    if (!trimmed) return 'Informe seu e-mail.';
    if (!isValidEmail(trimmed)) return 'Insira um e-mail válido. Ex.: nome@email.com';
    // Domínio parecido com um conhecido não é erro: o alerta "Você quis dizer…?"
    // (com o escape "Não, está correto") aparece ao tocar em Continuar.
    return ' ';
  };

  return (
    <>
      <style>{`
        ${FIELD_VALID_CHECK_CSS}
        ${SIGNUP_GREETING_CSS}
        /* Poppins loaded globally in index.html */

        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --border-subtle: #E5E7EB;
          --error: #D32F2F;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
          --shadow-lg: 0 12px 28px rgba(0,0,0,0.08);
        }

        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
          -webkit-font-smoothing: antialiased;
        }

        .page-shell {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          background: var(--bg-page);
        }

        /* padding-bottom usa --vvb (altura do teclado virtual quando aberto)
           e env(safe-area-inset-bottom) para garantir que o botão Continuar
           nunca fique escondido atrás do teclado em iOS/Android. */
        .inner-wrap {
          flex: 1;
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          box-sizing: border-box;
          transition: padding-bottom 0.18s ease;
        }

        .text-field {
          scroll-margin-bottom: calc(160px + env(safe-area-inset-bottom, 0px));
          scroll-margin-top: 1rem;
        }

        .header-block {
          text-align: center;
          max-width: 22rem;
          margin-top: 1rem;
          margin-bottom: 2rem;
        }

        .main-headline {
          font-size: 1.25rem;
          line-height: 1.35;
          font-weight: 600;
          color: var(--text-main);
          margin: 0 0 .75rem;
        }

        .sub-headline {
          font-size: .9rem;
          line-height: 1.4;
          font-weight: 400;
          color: var(--text-dim);
          margin: 0 auto;
        }

        .form-card {
          width: 100%;
          max-width: 400px;
          background: var(--surface);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm);
          padding: 1.25rem 1.25rem 1rem;
          border: 1px solid rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
          align-items: stretch;
          box-sizing: border-box;
        }

        .label-row {
          display: flex;
          flex-direction: column;
          margin-bottom: .75rem;
        }

        .label-title {
          font-size: .8rem;
          font-weight: 500;
          line-height: 1.3;
          color: var(--text-main);
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          width: 100%;
          box-sizing: border-box;
        }

        .label-hint {
          font-size: .7rem;
          font-weight: 400;
          color: var(--text-dim);
          line-height: 1.3;
        }

        .field-wrapper {
          width: 100%;
          background: var(--surface);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-subtle);
          box-shadow: none;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          padding: 0;
          overflow: hidden;
          transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }

        .text-field {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
          border: none;
          outline: none;
          font-size: 1.05rem;
          line-height: 1.4;
          color: var(--text-main);
          background: transparent;
          padding: 1rem 1.1rem;
          box-sizing: border-box;
          -webkit-appearance: none;
        }

        .text-field:focus {
          outline: none;
        }

        .field-wrapper:focus-within {
          border: 2px solid var(--primary);
          box-shadow: 0 0 0 3px rgba(211,47,47,0.10);
          background: #FFF;
        }

        .error-text {
          min-height: 1.25rem;
          font-size: .8rem;
          line-height: 1.4;
          font-weight: 500;
          color: var(--error);
          margin-top: .6rem;
          margin-bottom: 1rem;
        }

        .typo-alert {
          display: flex;
          flex-direction: column;
          gap: .55rem;
          background: #FFF8E1;
          border: 1px solid #FFD54F;
          border-radius: var(--radius-lg);
          padding: .75rem .9rem;
          margin-top: .6rem;
          margin-bottom: 1rem;
          font-size: .9rem;
          line-height: 1.4;
          color: #5D4037;
          animation: typoSlideIn .25s cubic-bezier(.4,0,.2,1) both;
        }
        .typo-alert__question {
          display: flex;
          align-items: center;
          gap: .5rem;
        }
        .typo-alert__icon {
          font-size: 1.1rem;
          flex-shrink: 0;
        }
        .typo-alert__suggestion {
          font-weight: 700;
          color: var(--primary);
        }
        .typo-alert__actions {
          display: flex;
          gap: .5rem;
        }
        .typo-alert__btn {
          flex: 1;
          padding: .55rem .8rem;
          border-radius: var(--radius-full);
          font-size: .85rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: background .18s ease, transform .18s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .typo-alert__btn--primary {
          background: var(--primary);
          color: #FFF;
          border: none;
        }
        .typo-alert__btn--primary:hover { background: #B71C1C; }
        .typo-alert__btn--primary:active { transform: scale(.98); }
        .typo-alert__btn--ghost {
          background: transparent;
          color: #5D4037;
          border: 1px solid rgba(93,64,55,0.25);
        }
        .typo-alert__btn--ghost:hover {
          background: rgba(93,64,55,0.06);
        }

        @keyframes typoSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .typo-alert { animation: none; }
        }

        .confirm-wrap {
          display: flex;
          justify-content: center;
          margin-top: .5rem;
          margin-bottom: .5rem;
        }

        .confirm-button {
          background: var(--primary);
          color: #FFF;
          border: none;
          border-radius: var(--radius-full);
          box-shadow: none;
          font-size: 1.05rem;
          line-height: 1.2;
          font-weight: 600;
          padding: 1rem 1.5rem;
          min-width: 200px;
          min-height: 48px;
          cursor: pointer;
          transition: background .18s ease, opacity .18s ease;
          -webkit-tap-highlight-color: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
        }
        .confirm-button__spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: ef-spin 0.8s linear infinite;
        }
        @keyframes ef-spin { to { transform: rotate(360deg); } }
        .confirm-button:hover:not(:disabled) {
          background: #B71C1C;
        }
        .confirm-button:focus-visible {
          box-shadow: 0 0 0 3px rgba(211,47,47,0.12);
        }
        .confirm-button:disabled {
          opacity: .6;
          cursor: not-allowed;
          box-shadow: var(--shadow-xs);
        }
        .confirm-button:active:not(:disabled) {
          transform: scale(.98);
          box-shadow: var(--shadow-sm);
        }

        .contact-link {
          margin-top: auto;
          text-align: center;
          font-size: .8rem;
          line-height: 1.4;
          color: var(--text-main);
          font-weight: 400;
          padding: 0 1rem calc(1.5rem + env(safe-area-inset-bottom));
          box-sizing: border-box;
        }

        .contact-link a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }

        @media (min-width: 480px) {
          .inner-wrap {
            padding: 5.5rem 2rem 4rem;
          }
          .main-headline {
            font-size: 1.4rem;
          }
          .sub-headline {
            font-size: .9rem;
          }
          .confirm-button {
            min-width: 220px;
            font-size: 1rem;
          }
        }

        @media (min-width: 768px) {
          .main-headline {
            font-size: 1.5rem;
          }
          .sub-headline {
            font-size: .9rem;
          }
        }
      `}</style>

      <div className="page-shell">
        <div className="inner-wrap">
          <FormBackButton
            onClick={() => {
              const variant = getSignupVariant();
              if (isFirstStep('/email', variant)) {
                navigate('/');
                return;
              }
              const prev = prevReachableSignupRoute('email', variant);
              navigate(prev || '/consulta');
            }}
          />

          <ProgressBar {...signupUxProgress('/email', getSignupVariant())} />

          <div className="header-block">
            {guided ? <SignupGreeting fullName={readSignupFullName()} /> : null}
            <h1 className="main-headline deco-headline">Qual é o seu e-mail?</h1>
            {usesEmailConfirm() ? (
              <p className="sub-headline">
                Na próxima etapa, solicitaremos que você digite novamente o mesmo e-mail (sem copiar
                e colar), para evitar erros de digitação nas receitas e nos documentos.
              </p>
            ) : (
              <p className="sub-headline">
                Usamos o e-mail para enviar suas receitas, atestados e o comprovante da consulta.
              </p>
            )}
            <DecoDivider variant="chevron" />
          </div>

          <form
            className="form-card"
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            noValidate
          >
            {guided ? (
              <SignupStepHeading
                title={signupStepTitle('/email')}
                subtitle={
                  usesEmailConfirm()
                    ? 'Na próxima etapa, solicitaremos que você digite novamente o mesmo e-mail (sem copiar e colar), para evitar erros de digitação nas receitas e nos documentos.'
                    : 'Usamos o e-mail para enviar suas receitas, atestados e o comprovante da consulta.'
                }
              />
            ) : null}
            <div className="label-row">
              <div className="label-title">
                <label htmlFor="patient-email">E-mail</label>
              </div>
              <div
                className="label-hint"
                id="patient-email-hint"
                onClick={() => { try { document.getElementById('patient-email')?.focus(); } catch {} }}
              >Ex.: "nome@email.com"</div>
            </div>

            <div className="field-wrapper deco-field-focus">
              <input
                id="patient-email"
                ref={inputRef}
                type="email"
                className="text-field"
                placeholder="nome@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSuggestion(null);
                  if (!touched) setTouched(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
                onFocus={(e) => {
                  const node = e.currentTarget;
                  setTimeout(() => {
                    try { node.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
                  }, 220);
                }}
                autoComplete="email"
                inputMode="email"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="next"
                aria-invalid={touched && !isValid && !suggestion}
                aria-describedby="patient-email-hint patient-email-error"
                aria-required="true"
              />
              {/* Sem check enquanto houver sugestão de typo: o campo é válido
                  em forma, mas ainda estamos perguntando se o domínio está
                  certo — um visto verde ali contradiria o alerta. */}
              {guided && <FieldValidCheck show={isValid && !suggestion} label="E-mail válido" />}
            </div>

            {suggestion ? (
              <div className="typo-alert" role="alert" id="patient-email-error">
                <div className="typo-alert__question">
                  <span className="typo-alert__icon" aria-hidden="true">✉️</span>
                  <span>
                    Você quis dizer{' '}
                    <span className="typo-alert__suggestion">{suggestion}</span>?
                  </span>
                </div>
                <div className="typo-alert__actions">
                  <button
                    type="button"
                    className="typo-alert__btn typo-alert__btn--primary"
                    onClick={acceptSuggestion}
                  >
                    Sim, é isso
                  </button>
                  <button
                    type="button"
                    className="typo-alert__btn typo-alert__btn--ghost"
                    onClick={rejectSuggestion}
                  >
                    Não, está correto
                  </button>
                </div>
              </div>
            ) : (
              <div className="error-text" id="patient-email-error" role={touched && !isValid ? 'alert' : undefined}>
                {getErrorMessage()}
              </div>
            )}

            <div className="confirm-wrap">
              <button
                type="submit"
                className="confirm-button"
                disabled={!isValid || isSubmitting}
              >
                {signupCta('email', { guided, submitting: isSubmitting })}
              </button>
            </div>
          </form>

        </div>
      </div>

      <AccessBlockedModal
        message={blockedMessage}
        onClose={() => {
          clearPendingAccessBlockedMessage();
          setBlockedMessage(null);
          navigate('/');
        }}
      />
    </>
  );
};

export default EmailForm;
